import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { API, useAuth } from '../../App';
import { useHubT, HUB_CATEGORIES } from './hubStrings';
import { CommunityReportPhotoUploader } from './CommunityReportPhotoUploader';

// Unified "Share Local Information" composer — same simple form for
// residents and Official Authorities (authorities get an expiry override).
// Feb 2026 — accepts `defaultCategory` so the Community Post chooser can
// jump the user straight to "Need Help", "Offer Help", etc.
export const ShareInfoDialog = ({ open, onClose, onShared, isAuthority, defaultCategory = '', existingReport = null }) => {
  const { user } = useAuth();
  const hubT = useHubT();
  const isEdit = !!existingReport;
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({
    town: '', category: '', title: '', details: '', truth: false, nopi: false,
    attachTo: '', expiryHours: '',
  });
  const [schedule, setSchedule] = useState({
    showImmediately: true,
    visibleFrom: '',
    eventStartAt: '',
    eventEndAt: '',
    autoExpireOnEventEnd: true,
    reminderHours: 0,
  });
  const [photos, setPhotos] = useState([]);
  const [attachable, setAttachable] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) axios.get(`${API}/incidents/meta`).then(r => setMeta(r.data)).catch(() => {});
  }, [open]);

  // Apply the preset category whenever the dialog opens (and the caller
  // provided one). Clears back to blank on close so a subsequent open with
  // no preset shows the default state.
  useEffect(() => {
    if (open && defaultCategory) {
      setForm(f => ({ ...f, category: defaultCategory }));
    }
  }, [open, defaultCategory]);

  // Edit-mode: pre-fill from the existing report each time the dialog
  // opens with one. Converts ISO strings back to the local datetime-local
  // format the inputs expect.
  useEffect(() => {
    if (!open || !existingReport) return;
    const isoToLocal = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setForm({
      town: existingReport.town || '',
      category: existingReport.category || '',
      title: existingReport.title || '',
      details: existingReport.body || '',
      truth: true,  // already accepted at original creation time
      nopi: true,
      attachTo: '',
      expiryHours: '',
    });
    setSchedule({
      showImmediately: !existingReport.visibleFrom,
      visibleFrom: isoToLocal(existingReport.visibleFrom),
      eventStartAt: isoToLocal(existingReport.eventStartAt),
      eventEndAt: isoToLocal(existingReport.eventEndAt),
      autoExpireOnEventEnd: existingReport.autoExpireOnEventEnd !== false,
      reminderHours: Number(existingReport.reminderHours || 0),
    });
    setPhotos(existingReport.photos || []);
  }, [open, existingReport]);

  useEffect(() => {
    if (!form.town || !form.category) { setAttachable([]); return; }
    axios.get(`${API}/safety-hub/attachable`, { params: { town: form.town, category: form.category } })
      .then(r => setAttachable((r.data.items || []).filter(it => !isEdit || it.id !== existingReport?.id)))
      .catch(() => setAttachable([]));
  }, [form.town, form.category, isEdit, existingReport?.id]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setSched = (k, v) => setSchedule(s => ({ ...s, [k]: v }));

  // Reset the scheduling section whenever the dialog is closed so the next
  // open starts fresh — otherwise stale future dates + a stale cross-field
  // error can greet the user on their next post.
  const resetSchedule = () => setSchedule({
    showImmediately: true, visibleFrom: '', eventStartAt: '', eventEndAt: '', autoExpireOnEventEnd: true, reminderHours: 0,
  });
  // Best-effort delete of any photos uploaded during the aborted compose so
  // Supabase storage doesn't accumulate orphans. The server will refuse to
  // delete anything already attached to a report (409), so this is safe.
  const purgeDraftPhotos = () => {
    // Never purge in edit mode — `photos` holds the existing report's already
    // attached photos, which must not be deleted when cancelling an edit.
    if (isEdit || !user?.token || photos.length === 0) return;
    photos.forEach((url) => {
      axios.delete(`${API}/community-reports/photo`, {
        params: { url },
        headers: { Authorization: `Bearer ${user.token}` },
      }).catch(() => { /* orphaned photos are self-healing on next cleanup */ });
    });
  };
  const handleClose = () => {
    purgeDraftPhotos();
    resetSchedule();
    setPhotos([]);
    onClose();
  };

  // datetime-local returns 'YYYY-MM-DDTHH:mm' in local time — convert to a
  // proper ISO string with timezone so the backend can compare correctly.
  const localToIso = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  // Cross-field validation before enabling submit.
  const scheduleValid = (() => {
    const vf = !schedule.showImmediately ? localToIso(schedule.visibleFrom) : null;
    const es = localToIso(schedule.eventStartAt);
    const ee = localToIso(schedule.eventEndAt);
    if (es && ee && ee <= es) return false;
    if (vf && es && vf > es) return false;
    return true;
  })();

  const canSubmit = form.town && form.category && form.title.trim().length >= 4
    && form.details.trim().length >= 10 && form.truth && form.nopi && scheduleValid;

  const submit = async () => {
    setBusy(true);
    try {
      const attached = attachable.find(a => a.id === form.attachTo);
      const payload = {
        town: form.town,
        category: form.category,
        title: form.title.trim(),
        body: form.details.trim(),
      };
      if (attached?.type === 'incident') payload.incidentId = attached.id;
      if (attached?.type === 'report') payload.attachToReportId = attached.id;
      if (isAuthority && form.expiryHours) payload.expiryHours = Number(form.expiryHours);
      if (photos.length > 0) payload.photos = photos;
      if (!schedule.showImmediately && schedule.visibleFrom) {
        payload.visibleFrom = localToIso(schedule.visibleFrom);
      }
      if (schedule.eventStartAt) payload.eventStartAt = localToIso(schedule.eventStartAt);
      if (schedule.eventEndAt) payload.eventEndAt = localToIso(schedule.eventEndAt);
      // autoExpireOnEventEnd only meaningful when an end date is set; server
      // treats the field as false in that case anyway.
      if (schedule.eventEndAt) payload.autoExpireOnEventEnd = !!schedule.autoExpireOnEventEnd;
      // Reminder only meaningful when an event start is set. Server drops any
      // value that would compute to a past reminderAt.
      if (schedule.eventStartAt && schedule.reminderHours > 0) {
        payload.reminderHours = Number(schedule.reminderHours);
      }
      if (isEdit) {
        // In edit mode we send the exact same payload shape; the backend
        // preserves town/category/authorship regardless of what we submit.
        await axios.patch(`${API}/community-reports/${existingReport.id}`, payload, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        toast.success(hubT('editSaved'));
      } else {
        await axios.post(`${API}/community-reports`, payload, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        toast.success(hubT('sharedOk'));
      }
      setForm({ town: '', category: '', title: '', details: '', truth: false, nopi: false, attachTo: '', expiryHours: '' });
      resetSchedule();
      setPhotos([]);
      onClose();
      onShared?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="share-info-dialog">
        <DialogHeader>
          <DialogTitle>{hubT('shareInfo')}</DialogTitle>
          <DialogDescription className="sr-only">{hubT('shareInfo')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-700">{hubT('affectedTown')}</label>
            <select value={form.town} onChange={set('town')} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="share-town-select">
              <option value="">—</option>
              {(meta?.towns || []).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">{hubT('categoryLabel')}</label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {HUB_CATEGORIES.map(c => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, category: c.key, attachTo: '' }))}
                  className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-xs font-semibold text-left transition-colors ${
                    form.category === c.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`share-cat-${c.key}`}
                >
                  {c.emoji} {hubT(c.labelKey)}
                </button>
              ))}
            </div>
          </div>

          {attachable.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="share-attach-list">
              <p className="text-xs font-semibold text-gray-700 mb-1.5">{hubT('attachPrompt')}</p>
              <label className="flex items-center gap-2 text-sm py-1">
                <input type="radio" name="attach" checked={!form.attachTo} onChange={() => setForm(f => ({ ...f, attachTo: '' }))} data-testid="share-attach-new" />
                {hubT('attachNew')}
              </label>
              {attachable.map(a => (
                <label key={a.id} className="flex items-center gap-2 text-sm py-1">
                  <input type="radio" name="attach" checked={form.attachTo === a.id} onChange={() => setForm(f => ({ ...f, attachTo: a.id }))} data-testid={`share-attach-${a.id}`} />
                  <span className="truncate">{a.title}{a.updateCount > 0 ? ` · ${a.updateCount} ${hubT('updates')}` : ''}</span>
                </label>
              ))}
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-gray-700">{hubT('titleLabel')}</label>
            <Input value={form.title} onChange={set('title')} maxLength={250} className="mt-1" data-testid="share-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">
              {hubT('detailsLabel')} <span className="font-normal text-gray-500">— {hubT('detailsMinHint')}</span>
            </label>
            <Textarea
              value={form.details}
              onChange={set('details')}
              rows={8}
              maxLength={10000}
              placeholder={hubT('detailsPlaceholder')}
              className="mt-1"
              data-testid="share-details-input"
            />
            {(() => {
              const len = form.details.trim().length;
              const remaining = 10 - len;
              return remaining > 0 ? (
                <p className="text-[11px] text-amber-700 mt-0.5" data-testid="share-details-hint">
                  {remaining === 1 ? hubT('detailsOneMore') : hubT('detailsMoreNeeded', { count: remaining })}
                </p>
              ) : (
                <p className="text-[11px] text-emerald-700 mt-0.5" data-testid="share-details-hint">
                  ✓ {hubT('detailsComplete')}
                </p>
              );
            })()}
          </div>

          {/* Alert / Event Dates — all optional, v6.3 (Aug 2026) */}
          <div className="border border-gray-200 rounded-lg p-3 bg-slate-50/60" data-testid="share-schedule-section">
            <label className="text-xs font-semibold text-gray-700">{hubT('scheduleTitle')}</label>
            <p className="text-[11px] text-gray-500 mt-0.5">{hubT('scheduleHelp')}</p>

            <label className="flex items-center gap-2 text-xs text-gray-700 mt-2">
              <input
                type="checkbox"
                checked={schedule.showImmediately}
                onChange={e => setSchedule(s => ({ ...s, showImmediately: e.target.checked, visibleFrom: e.target.checked ? '' : s.visibleFrom }))}
                data-testid="share-show-immediately"
              />
              {hubT('showImmediately')}
            </label>

            {!schedule.showImmediately && (
              <div className="mt-1.5">
                <label className="text-[11px] font-semibold text-gray-600">{hubT('showAlertFrom')}</label>
                <input
                  type="datetime-local"
                  value={schedule.visibleFrom}
                  onChange={e => setSched('visibleFrom', e.target.value)}
                  className="w-full border rounded-lg px-2.5 py-1.5 text-sm mt-0.5 bg-white"
                  data-testid="share-visible-from"
                />
              </div>
            )}

            <div className="grid grid-cols-1 gap-2 mt-2">
              <div>
                <label className="text-[11px] font-semibold text-gray-600">{hubT('eventStarts')}</label>
                <input
                  type="datetime-local"
                  value={schedule.eventStartAt}
                  onChange={e => setSched('eventStartAt', e.target.value)}
                  className="w-full border rounded-lg px-2.5 py-1.5 text-sm mt-0.5 bg-white"
                  data-testid="share-event-start"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-600">{hubT('eventEnds')}</label>
                <input
                  type="datetime-local"
                  value={schedule.eventEndAt}
                  onChange={e => setSched('eventEndAt', e.target.value)}
                  className="w-full border rounded-lg px-2.5 py-1.5 text-sm mt-0.5 bg-white"
                  data-testid="share-event-end"
                />
              </div>
            </div>

            {schedule.eventEndAt && (
              <label className="flex items-center gap-2 text-xs text-gray-700 mt-2">
                <input
                  type="checkbox"
                  checked={schedule.autoExpireOnEventEnd}
                  onChange={e => setSched('autoExpireOnEventEnd', e.target.checked)}
                  data-testid="share-auto-expire"
                />
                {hubT('autoExpireOnEnd')}
              </label>
            )}

            {schedule.eventStartAt && (
              <div className="mt-2" data-testid="share-reminder-row">
                <label className="text-[11px] font-semibold text-gray-600">{hubT('reminderLabel')}</label>
                <select
                  value={schedule.reminderHours}
                  onChange={e => setSched('reminderHours', Number(e.target.value))}
                  className="w-full border rounded-lg px-2.5 py-1.5 text-sm mt-0.5 bg-white"
                  data-testid="share-reminder-select"
                >
                  <option value={0}>{hubT('reminderNone')}</option>
                  <option value={1}>{hubT('reminder_1')}</option>
                  <option value={3}>{hubT('reminder_3')}</option>
                  <option value={6}>{hubT('reminder_6')}</option>
                  <option value={12}>{hubT('reminder_12')}</option>
                  <option value={24}>{hubT('reminder_24')}</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-0.5">{hubT('reminderHelp')}</p>
              </div>
            )}

            {!scheduleValid && (
              <p className="text-[11px] text-red-600 mt-2" data-testid="share-schedule-error">
                {hubT('scheduleInvalid')}
              </p>
            )}
          </div>

          <CommunityReportPhotoUploader
            photos={photos}
            onChange={setPhotos}
            disabled={busy}
          />

          {isAuthority && (
            <div>
              <label className="text-xs font-semibold text-gray-700">{hubT('expiryLabel')}</label>
              <select value={form.expiryHours} onChange={set('expiryHours')} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="share-expiry-select">
                <option value="">{hubT('expiryDefault')}</option>
                {[6, 12, 24, 48, 72, 168, 336].map(h => (
                  <option key={h} value={h}>{h < 48 ? `${h}h` : `${h / 24} ${hubT('days')}`}</option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-start gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={form.truth} onChange={e => setForm(f => ({ ...f, truth: e.target.checked }))} className="mt-0.5" data-testid="share-confirm-truth" />
            {hubT('confirmTruth')}
          </label>
          <label className="flex items-start gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={form.nopi} onChange={e => setForm(f => ({ ...f, nopi: e.target.checked }))} className="mt-0.5" data-testid="share-confirm-nopi" />
            {hubT('confirmNoPI')}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={busy} data-testid="share-cancel-btn">{hubT('cancel')}</Button>
          <Button onClick={submit} disabled={!canSubmit || busy} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="share-submit-btn">
            {busy ? '…' : hubT('submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareInfoDialog;
